import { z } from 'zod';
import { AppointmentSlots, BookedAppointment, CallContext, PriorityOffer } from './appointments.js';
import { SfObject } from './crm.js';
import { AI_TEST_IDENTITY_RE } from './ai-test-identity.js';
export const INTERNAL_AI_CALLS_PATH = '/internal/ai-calls';
export const INTERNAL_AI_AVAILABILITY_PATH = '/internal/ai-calls/availability';
/** Plan 1E: an incoming-only Twilio Voice token for a "Talk in browser" test. */
export const INTERNAL_AI_BROWSER_TOKEN_PATH = '/internal/ai-calls/browser-token';
/** The approved plan as the voice agent receives it (fenced as data in its instructions). */
export const PLAN_TEXT_MAX = 4_000;
/**
 * The seller's conversation history as the voice agent receives it (fenced as data in its instructions): the digest
 * of the seller's timeline (emails, replies, earlier calls), newest lines kept whole. Sam's email writer reads the same size.
 */
export const HISTORY_TEXT_MAX = 3_000;
export const IdempotencyKey = z.string().regex(/^[A-Za-z0-9:_-]{8,120}$/);
/** The engine's gate refusals (cti-api ai-voice/gate.ts AiGateBlock) plus call_in_progress (service.ts). */
export const AiCallBlockReason = z.enum([
    'ai_voice_unavailable', 'no_consent', 'consent_field_missing', 'no_phone', 'opted_out', 'blocked', 'dnc',
    'daily_cap', 'customer_ceiling', 'calling_hours', 'no_caller_id', 'not_admin_for_test', 'invalid_number', 'call_in_progress',
]);
/**
 * Not a gate decision: the call could not be attempted. in_flight = the same key is still being handled.
 * plan_rejected = the plan text failed cti-api's deterministic check (CF-9); nothing was dialed, and the
 * same plan will be rejected again, so it is final for that plan.
 */
export const AiCallFailReason = z.enum([
    'record_not_found', 'salesforce_error', 'gate_error', 'twilio_error', 'in_flight', 'unknown_user', 'plan_rejected',
]);
const SF_RECORD_ID = z.string().regex(/^[a-zA-Z0-9]{15,18}$/);
/**
 * record   A real call to the record's phone, with its approved plan. `context`, `slots` (plan 1D) and `priority` are
 *          structured data cti-api renders outside the plan fence; slots are never plan text. `history` is what
 *          already happened with the seller (seller-written text included), fenced as data after the instructions.
 * test     An admin's test number, optionally with a plan.
 * practice An admin's test number with a real record's plan and prompt (plan 1D decision 6): never books,
 *          converts or writes to Salesforce, and a transfer rings the admin who started it.
 * practice_browser  Plan 1E: a practice call that rings the admin's browser (`client:<clientIdentity>`)
 *          instead of a test number. The identity embeds the admin's user id; cti-api's gate checks it.
 */
export const InternalAiCallTarget = z.discriminatedUnion('kind', [
    z
        .object({
        kind: z.literal('record'),
        objectType: z.enum(['Lead', 'Opportunity']),
        recordId: SF_RECORD_ID,
        planText: z.string().min(1).max(PLAN_TEXT_MAX),
        context: CallContext.optional(),
        slots: AppointmentSlots.optional(),
        history: z.string().min(1).max(HISTORY_TEXT_MAX).optional(),
        priority: PriorityOffer.optional(),
    })
        .strict(),
    z.object({ kind: z.literal('test'), to: z.string().min(7).max(20), planText: z.string().max(PLAN_TEXT_MAX).nullable() }).strict(),
    z
        .object({
        kind: z.literal('practice'),
        objectType: z.enum(['Lead', 'Opportunity']),
        recordId: SF_RECORD_ID,
        to: z.string().min(7).max(20),
        planText: z.string().min(1).max(PLAN_TEXT_MAX),
        context: CallContext.optional(),
        slots: AppointmentSlots.optional(),
        priority: PriorityOffer.optional(),
    })
        .strict(),
    z
        .object({
        kind: z.literal('practice_browser'),
        objectType: z.enum(['Lead', 'Opportunity']),
        recordId: SF_RECORD_ID,
        clientIdentity: z.string().regex(AI_TEST_IDENTITY_RE),
        planText: z.string().min(1).max(PLAN_TEXT_MAX),
        context: CallContext.optional(),
        slots: AppointmentSlots.optional(),
        priority: PriorityOffer.optional(),
    })
        .strict(),
]);
/** POST /internal/ai-calls (cti-api), HMAC-signed. `userId` is the person who approved the plan (or the admin testing). */
export const InternalAiCallRequest = z
    .object({ orgId: z.string().uuid(), userId: z.string().uuid(), idempotencyKey: IdempotencyKey, target: InternalAiCallTarget })
    .strict();
export const InternalAiCallResponse = z.discriminatedUnion('result', [
    z.object({ result: z.literal('placed'), aiCallId: z.string().uuid() }),
    z.object({ result: z.literal('blocked'), reason: AiCallBlockReason, aiCallId: z.string().uuid() }),
    z.object({ result: z.literal('failed'), reason: AiCallFailReason, aiCallId: z.string().uuid().nullable() }),
]);
/**
 * GET /internal/ai-calls/availability (cti-api), HMAC-signed; relayed to admins by outreach-api.
 * `browserCalls` (plan 1E) is optional so an older cti-api, which never sends it, reads as "no browser tests".
 */
export const AiAvailability = z.object({ available: z.boolean(), testNumbers: z.array(z.string()), browserCalls: z.boolean().optional() });
/** POST /internal/ai-calls/browser-token (cti-api), HMAC-signed: `userId` is the admin who will take the call. */
export const InternalBrowserTokenRequest = z.object({ orgId: z.string().uuid(), userId: z.string().uuid() }).strict();
/** An incoming-only Voice token (no outgoing grant) for a fresh aitest identity. Never logged. */
export const InternalBrowserTokenResponse = z.object({
    token: z.string().min(1),
    identity: z.string().regex(AI_TEST_IDENTITY_RE),
    expiresAt: z.string(),
});
/** ai_calls.status / ai_calls.outcome (migration 0050). */
export const AiCallStatus = z.enum(['queued', 'ringing', 'in_progress', 'transferring', 'transferred', 'completed', 'failed', 'blocked']);
export const AiCallOutcome = z.enum([
    'qualified_transferred', 'qualified_callback', 'not_interested', 'do_not_call', 'voicemail', 'no_answer', 'busy',
    'failed', 'wrong_number', 'hung_up', 'transfer_failed', 'blocked', 'other',
    /** Plan 1D: the agent booked one of the offered slots (a stored ai_calls.appointment). */
    'appointment_set',
    /** The seller already has a consultation: the agent confirmed it instead of booking another. Completes the touch; no booking stands. */
    'already_booked',
]);
/** ai_call_writebacks.status (migration 0056). */
export const WritebackStatus = z.enum(['pending', 'running', 'done', 'partial', 'failed', 'skipped']);
export const WritebackChange = z.object({
    label: z.string(),
    before: z.string().nullable(),
    after: z.string().nullable(),
    kind: z.enum(['changed', 'kept', 'not_written', 'created', 'converted']),
});
export const WritebackSummary = z.object({
    status: WritebackStatus,
    changes: z.array(WritebackChange).max(80),
    error: z.string().nullable(),
    mayRetry: z.boolean(),
    /** Set when the write-back converted the Lead: results link to the new Opportunity. */
    convertedOpportunityId: z.string().nullable(),
    /** The new Opportunity's Salesforce link, built by the server (P6 M-3); null without a conversion or a connection. */
    convertedOpportunityUrl: z.string().url().nullable(),
});
/** One AI call touch on the campaign's results table. */
export const AiCallResult = z.object({
    touchId: z.string().uuid(),
    enrollmentId: z.string().uuid(),
    name: z.string().nullable(),
    sfObject: SfObject,
    sfRecordId: z.string(),
    recordUrl: z.string().url().nullable(),
    touchStatus: z.enum(['planned', 'held', 'queued', 'dialing', 'sent', 'failed', 'skipped']),
    dueAt: z.string(),
    attempts: z.number().int(),
    lastBlockReason: z.string().nullable(),
    aiCallId: z.string().uuid().nullable(),
    callStatus: AiCallStatus.nullable(),
    outcome: AiCallOutcome.nullable(),
    summary: z.string().nullable(),
    qualification: z.record(z.unknown()).nullable(),
    durationSeconds: z.number().int().nullable(),
    startedAt: z.string().nullable(),
    enrollmentStatus: z.string(),
    exitReason: z.string().nullable(),
    /** Owner or admin: may open the transcript. */
    mayReadTranscript: z.boolean(),
    /** The slot the agent booked (plan 1D), or null. */
    appointment: BookedAppointment.nullable(),
    /** The Salesforce write-back for this call (plan 1D), or null when there is none. */
    writeback: WritebackSummary.nullable(),
});
export const AiCallResultsResponse = z.object({ items: z.array(AiCallResult), nextCursor: z.string().nullable() });
export const TranscriptLine = z.object({ role: z.enum(['agent', 'caller', 'system']), text: z.string(), at: z.string().nullable() });
/**
 * One AI call's turn-taking numbers (cti-api bridge, spec 2026-10-09 §4.3), stored in
 * `ai_calls.turn_stats`. totalMs = endpointMs (how long after the caller stopped the turn was
 * declared over) + answerMs (turn over → first agent audio). Null blocks: no answered turn.
 */
export const AiCallTurnStats = z.object({
    v: z.literal(1),
    bargeIn: z.enum(['gate', 'instant']),
    turns: z.number().int().nonnegative(),
    totalMs: z.object({ p50: z.number(), p90: z.number(), max: z.number() }).nullable(),
    endpointMs: z.object({ p50: z.number() }).nullable(),
    answerMs: z.object({ p50: z.number() }).nullable(),
    interruptions: z.number().int().nonnegative(),
    listening: z.number().int().nonnegative(),
    silenceChecks: z.number().int().nonnegative(),
});
export const AiCallTranscript = z.object({
    aiCallId: z.string().uuid(),
    lines: z.array(TranscriptLine),
    /** Null for calls made before turn stats existed. */
    turnStats: AiCallTurnStats.nullable(),
});
/** POST /api/ai-calls/test (admin): "Test call to my phone". */
export const TestCallRequest = z.object({ to: z.string().min(7).max(20) });
export const TestCallResponse = InternalAiCallResponse;
/** POST /api/call-plans/:enrollmentId/practice (admin): ring a test number with this lead's plan `version`. */
export const PracticeCallRequest = z.object({ version: z.number().int().min(1), to: z.string().min(7).max(20) }).strict();
/** One practice call on the campaign's list (ai_practice_calls, 0056). */
export const PracticeCall = z.object({
    id: z.string().uuid(),
    enrollmentId: z.string().uuid(),
    name: z.string().nullable(),
    sfObject: SfObject,
    sfRecordId: z.string(),
    planVersion: z.number().int(),
    aiCallId: z.string().uuid().nullable(),
    callStatus: AiCallStatus.nullable(),
    outcome: AiCallOutcome.nullable(),
    summary: z.string().nullable(),
    appointment: BookedAppointment.nullable(),
    result: InternalAiCallResponse.nullable(),
    createdAt: z.string(),
});
export const PracticeCallsResponse = z.object({ items: z.array(PracticeCall) });
//# sourceMappingURL=ai-calls.js.map